import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../layouts/DashboardLayout';
import { qrApi, productsApi, regionsApi } from '../lib/api';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  MagnifyingGlass, Printer, CircleNotch, Eye, Warning,
  ArrowsClockwise, ArrowLeft, QrCode, Package, Prohibit, PencilSimple,
} from '@phosphor-icons/react';
import { formatDateTime, getStatusColor, formatApiErrorDetail } from '../lib/utils';
import PrintPreviewDialog from '../components/PrintPreviewDialog';
import { toast } from 'sonner';

function stageToApiValue(stage) {
  return String(stage || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
}

export default function MasterQRDetailPage() {
  const { t } = useTranslation();
  const { masterQrId } = useParams();
  const decodedMasterQrId = decodeURIComponent(masterQrId);
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [masterQR, setMasterQR] = useState(null);
  const [children, setChildren] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [selectedQR, setSelectedQR] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [reprintOpen, setReprintOpen] = useState(false);
  const [reprintReason, setReprintReason] = useState('');
  const [reprintQrId, setReprintQrId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkLocation, setBulkLocation] = useState('');
  const [bulkNotes, setBulkNotes] = useState('');

  const [mapOpen, setMapOpen] = useState(false);
  const [mapProductId, setMapProductId] = useState('');
  const [mapBatchNumber, setMapBatchNumber] = useState('');
  const [mapMfgDate, setMapMfgDate] = useState('');
  const [mapExpiryDate, setMapExpiryDate] = useState('');

  const [subMasters, setSubMasters] = useState([]);
  const [subOpen, setSubOpen] = useState(false);
  const [subDestination, setSubDestination] = useState('');
  const [subNotes, setSubNotes] = useState('');

  const [regions, setRegions] = useState([]);
  const [selectedRegionIds, setSelectedRegionIds] = useState([]);
  const [savingRegions, setSavingRegions] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [printPreviewLabels, setPrintPreviewLabels] = useState([]);
  const [printPreviewTitle, setPrintPreviewTitle] = useState('QR Labels');

  const [lifecycleOpen, setLifecycleOpen] = useState(false);
  const [lifecycleStatus, setLifecycleStatus] = useState('decommissioned');
  const [lifecycleReason, setLifecycleReason] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [masterRes, childrenRes, prodRes, subRes, regRes] = await Promise.all([
        qrApi.get(decodedMasterQrId),
        qrApi.listChildren(decodedMasterQrId),
        productsApi.list(),
        qrApi.listSubMasters(decodedMasterQrId).catch(() => ({ data: [] })),
        regionsApi.list().catch(() => ({ data: [] })),
      ]);
      setMasterQR(masterRes.data);
      setChildren(childrenRes.data);
      setProducts(prodRes.data);
      setSubMasters(subRes.data || []);
      setRegions(regRes.data || []);
      setSelectedRegionIds(masterRes.data?.allowed_region_ids || []);
      const master = masterRes.data;
      if (master?.mapping_status === 'unmapped') {
        setMapProductId(master.intended_product_id || master.product_id || '');
        setMapBatchNumber(master.batch_label || '');
      }
    } catch {
      toast.error('Failed to load QR codes');
    } finally {
      setLoading(false);
    }
  }, [decodedMasterQrId]);

  useEffect(() => { loadData(); }, [loadData]);

  const getProduct = (productId) => products.find(p => p.id === productId);

  const bulkStageOptions = useMemo(() => {
    const product = getProduct(masterQR?.product_id);
    const stages = masterQR?.scan_stages?.length
      ? masterQR.scan_stages
      : (product?.scan_stages || []);
    if (stages.length) {
      return stages.map((stage) => ({ label: stage, value: stageToApiValue(stage) }));
    }
    return [
      { label: 'Received', value: 'received' },
      { label: 'Inspected', value: 'inspected' },
      { label: 'Packed', value: 'packed' },
      { label: 'Shipped', value: 'shipped' },
      { label: 'In transit', value: 'in_transit' },
      { label: 'Delivered', value: 'delivered' },
    ];
  }, [masterQR, products]);

  const printLabelsLocally = useCallback(async (qrIds) => {
    if (!qrIds.length) {
      toast.error('No QR codes selected');
      return;
    }
    setPrinting(true);
    try {
      const res = await qrApi.batchPrint(qrIds);
      const results = res.data?.results || [];
      const allowed = results.filter((r) => r.allowed);
      const denied = results.filter((r) => !r.allowed);
      if (!allowed.length) {
        toast.error(denied.length ? `${denied.length} label(s) need reprint approval` : 'Nothing to print');
        return;
      }
      const missingImages = allowed.filter((r) => !r.qr_image);
      if (missingImages.length === allowed.length) {
        toast.error('QR images are missing. Regenerate or refresh this batch and try again.');
        return;
      }
      setPrintPreviewLabels(
        allowed.map((r) => ({
          qr_image: r.qr_image,
          product_name: r.product_name,
          sku: r.sku,
          qr_id: r.qr_id,
          unit_label: r.unit_label,
          unit_display: r.unit_display,
          unit_code: r.unit_code,
        })),
      );
      setPrintPreviewTitle(`Batch labels (${allowed.length})`);
      setPrintPreviewOpen(true);
      if (denied.length) {
        toast.warning(`Ready to print ${allowed.length}; ${denied.length} skipped (reprint required)`);
      }
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || error.message || 'Failed to prepare print');
    } finally {
      setPrinting(false);
    }
  }, [loadData]);

  const filtered = useMemo(() => {
    return children.filter(qr => {
      if (filterStatus !== 'all' && qr.status !== filterStatus) return false;
      if (search && qr.qr_id.toLowerCase() !== search.trim().toLowerCase()) return false;
      return true;
    });
  }, [children, filterStatus, search]);

  const toggleSelect = (qrId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(qrId)) next.delete(qrId); else next.add(qrId);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(q => q.qr_id)));
    }
  };

  const handleDirectPrint = async (qr) => {
    setPrinting(true);
    try {
      const res = await qrApi.recordPrint(qr.qr_id);
      if (!res.data.allowed) {
        setReprintQrId(qr.qr_id);
        setReprintReason('');
        setReprintOpen(true);
        return;
      }
      const product = getProduct(qr.product_id);
      const image = res.data.qr_image || qr.qr_image;
      if (!image) {
        toast.error('QR image is missing for this code.');
        return;
      }
      setPrintPreviewLabels([{
        qr_image: image,
        product_name: product?.name,
        sku: product?.sku,
        qr_id: qr.qr_id,
        unit_label: qr.unit_label,
        unit_display: qr.unit_display,
        unit_code: qr.unit_code,
      }]);
      setPrintPreviewTitle(`QR ${qr.qr_id}`);
      setPrintPreviewOpen(true);
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || error.message || 'Failed to prepare print');
    } finally {
      setPrinting(false);
    }
  };

  const handlePrintSelected = () => {
    const ids = selectedIds.size > 0 ? [...selectedIds] : filtered.map((q) => q.qr_id);
    printLabelsLocally(ids);
  };

  const handleLifecycleApply = async () => {
    setSubmitting(true);
    try {
      const payload = {
        lifecycle_status: lifecycleStatus,
        reason: lifecycleReason || undefined,
      };
      if (selectedIds.size > 0) {
        await qrApi.bulkLifecycle({ ...payload, qr_ids: [...selectedIds] });
        toast.success(`Updated ${selectedIds.size} QR code(s)`);
      } else {
        const res = await qrApi.bulkLifecycle({ ...payload, master_qr_id: decodedMasterQrId });
        toast.success(`Updated ${res.data.updated_count} QR code(s)`);
      }
      setLifecycleOpen(false);
      setLifecycleReason('');
      setSelectedIds(new Set());
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to update lifecycle');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSingleLifecycle = async (qrId, status) => {
    const reason = window.prompt(`Reason for ${status}? (optional)`) || undefined;
    try {
      await qrApi.setLifecycle(qrId, { lifecycle_status: status, reason });
      toast.success(`QR marked as ${status}`);
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to update');
    }
  };

  const handleReprintRequest = async () => {
    if (!reprintReason.trim()) { toast.error('Please provide a reason'); return; }
    setSubmitting(true);
    try {
      await qrApi.requestReprint(reprintQrId, reprintReason);
      toast.success('Reprint request submitted for approval');
      setReprintOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const handleBulkUpdate = async () => {
    if (!bulkStatus) { toast.error('Select a status'); return; }
    setSubmitting(true);
    try {
      const data = {
        master_qr_id: decodedMasterQrId,
        new_status: bulkStatus,
        location: bulkLocation || undefined,
        notes: bulkNotes || undefined,
      };
      const res = await qrApi.bulkUpdate(data);
      toast.success(`Updated ${res.data.updated_count} QR codes to "${bulkStatus}"`);
      setBulkOpen(false);
      setSelectedIds(new Set());
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setSubmitting(false); }
  };

  const handleBulkUpdateSelected = async () => {
    if (!bulkStatus) { toast.error('Select a status'); return; }
    setSubmitting(true);
    try {
      const data = {
        qr_ids: [...selectedIds],
        new_status: bulkStatus,
        location: bulkLocation || undefined,
        notes: bulkNotes || undefined,
      };
      const res = await qrApi.bulkUpdate(data);
      toast.success(`Updated ${res.data.updated_count} QR codes to "${bulkStatus}"`);
      setBulkOpen(false);
      setSelectedIds(new Set());
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setSubmitting(false); }
  };

  const handleMapProduct = async () => {
    if (!mapProductId) {
      toast.error(t('generateQr.chooseProd'));
      return;
    }
    setSubmitting(true);
    try {
      await qrApi.mapMasterProduct(decodedMasterQrId, {
        product_id: mapProductId,
        batch_number: mapBatchNumber || undefined,
        manufacturing_date: mapMfgDate || undefined,
        expiry_date: mapExpiryDate || undefined,
      });
      toast.success(t('qr.mappingComplete'));
      setMapOpen(false);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to map batch');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateSubMaster = async () => {
    const itemIds = selectedIds.size > 0 ? [...selectedIds] : children.map((q) => q.qr_id);
    if (!itemIds.length) {
      toast.error('No item QRs available for shipment');
      return;
    }
    setSubmitting(true);
    try {
      const res = await qrApi.createSubMasterShipment({
        master_qr_id: decodedMasterQrId,
        item_qr_ids: itemIds,
        notes: subNotes || undefined,
        geofence: subDestination.trim()
          ? { name: subDestination.trim() }
          : undefined,
      });
      toast.success(t('qr.subMasterCreated'));
      setSubOpen(false);
      setSubDestination('');
      setSubNotes('');
      setSelectedIds(new Set());
      loadData();
      if (res.data?.qr_id) {
        setSelectedQR(res.data);
        setDetailOpen(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create SubMaster');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleRegion = (regionId) => {
    setSelectedRegionIds((prev) =>
      prev.includes(regionId) ? prev.filter((id) => id !== regionId) : [...prev, regionId]
    );
  };

  const handleSaveRegions = async () => {
    setSavingRegions(true);
    try {
      await qrApi.setMasterRegions(decodedMasterQrId, { allowed_region_ids: selectedRegionIds });
      toast.success('Allowed regions updated for this batch');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update regions');
    } finally {
      setSavingRegions(false);
    }
  };

  const isUnmapped = masterQR?.mapping_status === 'unmapped';
  const intendedProductName = masterQR?.intended_product_name
    || getProduct(masterQR?.intended_product_id)?.name;
  const product = masterQR && !isUnmapped ? getProduct(masterQR.product_id) : null;
  const headerProductName = product?.name || intendedProductName || t('qr.batchQrCodes');

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="master-qr-detail-page">
        {/* Back + Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/qr-codes')}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-zinc-800 text-zinc-400' : 'hover:bg-zinc-100 text-zinc-500'}`}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className={`text-2xl font-heading font-semibold ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
                {headerProductName}
              </h1>
              <p className={`text-sm mt-0.5 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                {isUnmapped && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium mr-2 ${isDark ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-800'}`}>
                    {t('qr.awaitingMapBadge')}
                  </span>
                )}
                {masterQR?.batch_label && (
                  <span className={`font-mono mr-2 ${isDark ? 'text-brand-400' : 'text-brand-600'}`}>{masterQR.batch_label}</span>
                )}
                {product?.batch_number && (
                  <span className={`font-mono mr-2 ${isDark ? 'text-brand-400' : 'text-brand-600'}`}>{product.batch_number}</span>
                )}
                {filtered.length} {t('qr.totalQrCodes')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isUnmapped && (
              <Button
                onClick={() => setMapOpen(true)}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="map-product-btn"
              >
                {t('qr.mapToProduct')}
              </Button>
            )}
            {!isUnmapped && (
              <Button
                onClick={() => { setSubDestination(''); setSubNotes(''); setSubOpen(true); }}
                variant="outline"
                className={isDark ? 'border-zinc-700 text-zinc-300' : ''}
                data-testid="create-submaster-btn"
              >
                {t('qr.createSubMaster')}
                {selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
              </Button>
            )}
            {selectedIds.size > 0 ? (
              <>
                <Button
                  onClick={handlePrintSelected}
                  variant="outline"
                  disabled={printing}
                  className={isDark ? 'border-zinc-700 text-zinc-300' : ''}
                  data-testid="print-selected-btn"
                >
                  {printing ? <CircleNotch className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
                  Print ({selectedIds.size})
                </Button>
                <Button
                  onClick={() => { setLifecycleStatus('decommissioned'); setLifecycleReason(''); setLifecycleOpen(true); }}
                  variant="outline"
                  className={isDark ? 'border-zinc-700 text-zinc-300' : ''}
                  data-testid="lifecycle-selected-btn"
                >
                  <Prohibit className="w-4 h-4 mr-2" />
                  Lifecycle
                </Button>
                <Button
                  onClick={() => { setBulkStatus(bulkStageOptions[0]?.value || ''); setBulkLocation(''); setBulkNotes(''); setBulkOpen(true); }}
                  className="bg-brand-600 hover:bg-brand-700 text-white"
                  data-testid="bulk-update-selected-btn"
                >
                  <PencilSimple className="w-4 h-4 mr-2" />
                  {t('qr.updateSelected')} ({selectedIds.size})
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={handlePrintSelected}
                  variant="outline"
                  disabled={printing || !filtered.length}
                  className={isDark ? 'border-zinc-700 text-zinc-300' : ''}
                  data-testid="print-all-btn"
                >
                  {printing ? <CircleNotch className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
                  Print all
                </Button>
                <Button
                  onClick={() => { setBulkStatus(bulkStageOptions[0]?.value || ''); setBulkLocation(''); setBulkNotes(''); setBulkOpen(true); }}
                  variant="outline"
                  className={isDark ? 'border-zinc-700 text-zinc-300' : ''}
                  data-testid="bulk-update-all-btn"
                >
                  <PencilSimple className="w-4 h-4 mr-2" />
                  {t('qr.updateAll')}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Master QR Card */}
        {masterQR && (
          <Card className={`${isDark ? 'bg-brand-900/10 border-brand-500/20' : 'bg-brand-50/60 border-brand-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className={`shrink-0 p-2 rounded-lg ${isDark ? 'bg-zinc-900' : 'bg-white'}`}>
                  {masterQR.qr_image ? (
                    <img src={`data:image/png;base64,${masterQR.qr_image}`} alt="Master QR" className="w-16 h-16 object-contain" />
                  ) : (
                    <Package className="w-12 h-12 text-brand-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-brand-600 text-white">{t('qr.master')}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${getStatusColor(masterQR.status)}`}>
                      {masterQR.status}
                    </span>
                    {isUnmapped && intendedProductName && (
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${isDark ? 'bg-zinc-800 text-zinc-300' : 'bg-white text-zinc-600'}`}>
                        {t('qr.intendedProductBadge')}: {intendedProductName}
                      </span>
                    )}
                  </div>
                  <p className={`text-xs font-mono mt-1 truncate ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                    {masterQR.qr_id}
                  </p>
                </div>
                <div className={`text-right ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                  <p className="text-2xl font-bold text-brand-500">{masterQR.child_count || children.length}</p>
                  <p className="text-xs">total QR codes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {masterQR && regions.length > 0 && (
          <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} shadow-sm`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <p className={`text-sm font-medium ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>Allowed scan regions</p>
                  <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
                    Field scans must occur inside a selected region (GPS required).
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={handleSaveRegions}
                  disabled={savingRegions}
                  className="bg-brand-600 hover:bg-brand-700 text-white"
                  data-testid="save-master-regions-btn"
                >
                  {savingRegions ? <CircleNotch className="w-4 h-4 animate-spin" /> : 'Save regions'}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {regions.map((region) => {
                  const active = selectedRegionIds.includes(region.id);
                  return (
                    <button
                      key={region.id}
                      type="button"
                      onClick={() => toggleRegion(region.id)}
                      className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${
                        active
                          ? 'bg-brand-600 text-white border-brand-600'
                          : isDark
                            ? 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
                            : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'
                      }`}
                    >
                      {region.name}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {subMasters.length > 0 && (
          <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} shadow-sm`}>
            <CardContent className="p-4">
              <h2 className={`text-sm font-semibold mb-3 ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
                {t('qr.subMasters')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {subMasters.map((sub) => (
                  <div
                    key={sub.qr_id}
                    className={`rounded-lg border p-3 flex gap-3 items-start ${isDark ? 'border-zinc-700 bg-zinc-800/40' : 'border-zinc-200'}`}
                    data-testid={`submaster-${sub.qr_id}`}
                  >
                    <div className="bg-white p-1 rounded shrink-0">
                      {sub.qr_image ? (
                        <img src={`data:image/png;base64,${sub.qr_image}`} alt="" className="w-12 h-12 object-contain" />
                      ) : (
                        <Package className="w-12 h-12 text-brand-500" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-violet-600 text-white">
                        {t('qr.subMaster')}
                      </span>
                      <p className={`text-xs font-mono mt-1 truncate ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>{sub.qr_id}</p>
                      <p className={`text-xs mt-1 ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>
                        {sub.child_count || (sub.child_qr_ids || []).length} {t('qr.itemsInCarton')}
                      </p>
                      {sub.geofence?.name && (
                        <p className={`text-xs mt-0.5 ${isDark ? 'text-brand-400' : 'text-brand-600'}`}>{sub.geofence.name}</p>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 mt-1 px-2"
                        onClick={() => { setSelectedQR(sub); setDetailOpen(true); }}
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} shadow-sm`}>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <MagnifyingGlass className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`} />
                <Input placeholder={t('qr.searchQrId')} value={search} onChange={e => setSearch(e.target.value)}
                  className={`pl-9 ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''}`} data-testid="search-child-input" />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="filter-child-status">
                  <SelectValue placeholder={t('qr.allStatuses')} />
                </SelectTrigger>
                <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}>
                  <SelectItem value="all">{t('qr.allStatuses')}</SelectItem>
                  <SelectItem value="created">{t('qr.created')}</SelectItem>
                  <SelectItem value="in_transit">{t('qr.inTransit')}</SelectItem>
                  <SelectItem value="shipped">{t('qr.shipped')}</SelectItem>
                  <SelectItem value="delivered">{t('qr.delivered')}</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={filtered.length > 0 && selectedIds.size === filtered.length}
                  onCheckedChange={selectAll}
                  className={isDark ? 'border-zinc-600' : 'border-zinc-300'}
                  data-testid="select-all-checkbox"
                />
                <span className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>{t('common.selectAll')}</span>
              </div>
              <div className={`text-sm flex items-center justify-end ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                {filtered.length} of {children.length}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Child QR Tiles */}
        {loading ? (
          <div className="flex justify-center py-16">
            <CircleNotch className="w-8 h-8 animate-spin text-brand-500" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
            <CardContent className="py-16 text-center">
              <QrCode className={`w-12 h-12 mx-auto mb-3 opacity-30 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`} />
              <p className={isDark ? 'text-zinc-500' : 'text-zinc-400'}>{t('qr.noQrCodes')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {filtered.map(qr => {
              const qrProduct = getProduct(qr.product_id);
              return (
                <div
                  key={qr.qr_id}
                  className={`group relative rounded-lg border overflow-hidden transition-all hover:shadow-md ${
                    isDark
                      ? 'bg-zinc-900 border-zinc-800 hover:border-zinc-700'
                      : 'bg-white border-zinc-200 hover:border-zinc-300'
                  } ${selectedIds.has(qr.qr_id) ? (isDark ? 'ring-2 ring-brand-500' : 'ring-2 ring-brand-400') : ''}`}
                  data-testid={`qr-tile-${qr.qr_id}`}
                >
                  {/* Checkbox */}
                  <div className="absolute top-2 left-2 z-10">
                    <Checkbox
                      checked={selectedIds.has(qr.qr_id)}
                      onCheckedChange={() => toggleSelect(qr.qr_id)}
                      className={isDark ? 'border-zinc-600' : 'border-zinc-300'}
                    />
                  </div>

                  {/* QR Image */}
                  <div className="flex justify-center p-3 bg-white">
                    {qr.qr_image ? (
                      <img src={`data:image/png;base64,${qr.qr_image}`} alt="QR"
                        className="w-full aspect-square object-contain max-w-[120px]" />
                    ) : (
                      <div className="w-full aspect-square max-w-[120px] flex items-center justify-center">
                        <QrCode className="w-10 h-10 text-zinc-300" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-2.5 space-y-1.5">
                    <p className={`text-xs font-medium truncate ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>
                      {qrProduct?.name || intendedProductName || (isUnmapped ? t('qr.awaitingMapBadge') : 'Unknown')}
                    </p>
                    <div className="flex items-center justify-between gap-1 flex-wrap">
                      {qr.lifecycle_status && qr.lifecycle_status !== 'active' ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-500/15 text-red-600 border border-red-500/30">
                          {qr.lifecycle_status}
                        </span>
                      ) : (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${getStatusColor(qr.status)}`}>
                          {qr.status}
                        </span>
                      )}
                      <span className={`text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                        {qr.scan_count || 0} scans
                      </span>
                    </div>
                    {(qr.print_count || 0) >= 1 && (
                      <div className="flex items-center gap-1">
                        <Warning className="w-3 h-3 text-yellow-500" />
                        <span className={`text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                          Printed {qr.print_count}x
                        </span>
                      </div>
                    )}
                    <div className="flex gap-1 pt-1">
                      <Button size="sm" variant="ghost"
                        onClick={() => { setSelectedQR(qr); setDetailOpen(true); }}
                        className={`flex-1 h-7 text-xs ${isDark ? 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800' : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100'}`}
                        data-testid={`view-qr-${qr.qr_id}`}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost"
                        onClick={() => handleDirectPrint(qr)}
                        className={`flex-1 h-7 text-xs ${isDark ? 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800' : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100'}`}
                        data-testid={`print-qr-${qr.qr_id}`}>
                        <Printer className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className={`${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-100' : 'bg-white'} max-w-lg max-h-[90vh] overflow-y-auto`}>
            <DialogHeader><DialogTitle className="font-heading">{t('qr.qrDetails')}</DialogTitle></DialogHeader>
            {selectedQR && (
              <div className="space-y-4">
                {selectedQR.qr_image && (
                  <div className="flex justify-center p-4 bg-white rounded-md border">
                    <img src={`data:image/png;base64,${selectedQR.qr_image}`} alt="QR" className="w-48 h-48 object-contain" />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className={isDark ? 'text-zinc-500' : 'text-zinc-400'}>{t('qr.qrId')}</span><p className="font-mono text-xs mt-0.5">{selectedQR.qr_id}</p></div>
                  <div><span className={isDark ? 'text-zinc-500' : 'text-zinc-400'}>{t('common.status')}</span><p className="mt-0.5"><span className={`px-2 py-0.5 rounded-full text-xs border ${getStatusColor(selectedQR.status)}`}>{selectedQR.status}</span></p></div>
                  <div><span className={isDark ? 'text-zinc-500' : 'text-zinc-400'}>Lifecycle</span><p className="mt-0.5 capitalize">{selectedQR.lifecycle_status || 'active'}</p></div>
                  <div><span className={isDark ? 'text-zinc-500' : 'text-zinc-400'}>{t('qr.product')}</span><p className="mt-0.5">{getProduct(selectedQR.product_id)?.name || 'Unknown'}</p></div>
                  <div><span className={isDark ? 'text-zinc-500' : 'text-zinc-400'}>{t('qr.printCount')}</span><p className="mt-0.5">{selectedQR.print_count || 0}</p></div>
                  {selectedQR.fingerprint && <div><span className={isDark ? 'text-zinc-500' : 'text-zinc-400'}>{t('qr.fingerprint')}</span><p className="font-mono text-xs mt-0.5">{selectedQR.fingerprint}</p></div>}
                </div>
                {selectedQR.scan_stages && (
                  <div>
                    <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>{t('qr.scanStages')}</span>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {selectedQR.scan_stages.map((s, i) => (
                        <span key={i} className={`px-2 py-0.5 rounded text-xs ${isDark ? 'bg-zinc-800 text-zinc-300' : 'bg-slate-100 text-slate-700'}`}>{i + 1}. {s}</span>
                      ))}
                    </div>
                  </div>
                )}
                {selectedQR.status_history && selectedQR.status_history.length > 0 && (
                  <div>
                    <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>{t('qr.history')}</span>
                    <div className="space-y-1 mt-1 max-h-32 overflow-y-auto">
                      {selectedQR.status_history.map((h, i) => (
                        <div key={i} className={`text-xs p-1.5 rounded ${isDark ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                          <span className="font-medium capitalize">{h.status}</span>
                          <span className={isDark ? 'text-zinc-500' : 'text-zinc-400'}> - {formatDateTime(h.timestamp)}</span>
                          {h.bulk && <span className="ml-1 text-brand-500">(bulk)</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-800/50">
                  <Button size="sm" variant="outline" onClick={() => handleDirectPrint(selectedQR)} className={isDark ? 'border-zinc-700' : ''}>
                    <Printer className="w-4 h-4 mr-1" /> Print
                  </Button>
                  {(selectedQR.lifecycle_status || 'active') === 'active' ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => handleSingleLifecycle(selectedQR.qr_id, 'decommissioned')} className={isDark ? 'border-zinc-700 text-amber-500' : 'text-amber-600'}>
                        Decommission
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleSingleLifecycle(selectedQR.qr_id, 'blacklisted')} className={isDark ? 'border-zinc-700 text-red-400' : 'text-red-600'}>
                        Blacklist
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => handleSingleLifecycle(selectedQR.qr_id, 'active')} className={isDark ? 'border-zinc-700 text-emerald-400' : 'text-emerald-600'}>
                      Restore active
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reprint Dialog */}
        <Dialog open={reprintOpen} onOpenChange={setReprintOpen}>
          <DialogContent className={`${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-100' : 'bg-white'} max-w-md`}>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Warning className="w-5 h-5 text-yellow-500" /> {t('qr.reprintRequired')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                {t('qr.reprintMsg')}
              </p>
              <div className="space-y-2">
                <Label>{t('qr.reason')} *</Label>
                <Input value={reprintReason} onChange={e => setReprintReason(e.target.value)}
                  placeholder="e.g. Label damaged" className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}
                  data-testid="reprint-reason-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReprintOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleReprintRequest} disabled={submitting || !reprintReason.trim()}
                className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="submit-reprint-btn">
                {submitting ? <CircleNotch className="w-4 h-4 animate-spin" /> : t('qr.submitRequest')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Update Dialog */}
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent className={`${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-100' : 'bg-white'} max-w-md`}>
            <DialogHeader>
              <DialogTitle className="font-heading">{t('qr.bulkUpdate')}</DialogTitle>
              <DialogDescription className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>
                {selectedIds.size > 0
                  ? `Update ${selectedIds.size} selected QR code(s)`
                  : `Update all ${children.length} QR codes in this batch`
                }
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>New Status *</Label>
                <Select value={bulkStatus} onValueChange={setBulkStatus}>
                  <SelectTrigger className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="bulk-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}>
                    {bulkStageOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('qr.location')}</Label>
                <Input value={bulkLocation} onChange={e => setBulkLocation(e.target.value)}
                  placeholder="e.g. Warehouse B" className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}
                  data-testid="bulk-location-input" />
              </div>
              <div className="space-y-2">
                <Label>{t('qr.notes')}</Label>
                <Input value={bulkNotes} onChange={e => setBulkNotes(e.target.value)}
                  placeholder="Optional notes" className={isDark ? 'bg-zinc-800 border-zinc-700' : ''} />
              </div>
              {selectedIds.size === 0 && (
                <div className={`p-2 rounded border text-xs ${isDark ? 'bg-brand-900/20 border-brand-500/30 text-brand-400' : 'bg-brand-50 border-brand-200 text-brand-600'}`}>
                  Using Master QR - all child QR codes will be updated.
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
              <Button
                onClick={selectedIds.size > 0 ? handleBulkUpdateSelected : handleBulkUpdate}
                disabled={submitting || !bulkStatus}
                className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="confirm-bulk-btn">
                {submitting ? <CircleNotch className="w-4 h-4 animate-spin" /> : selectedIds.size > 0 ? `Update ${selectedIds.size} QR(s)` : 'Update All'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Lifecycle dialog */}
        <Dialog open={lifecycleOpen} onOpenChange={setLifecycleOpen}>
          <DialogContent className={`${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-100' : 'bg-white'} max-w-md`}>
            <DialogHeader>
              <DialogTitle className="font-heading">QR lifecycle</DialogTitle>
              <DialogDescription className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>
                {selectedIds.size > 0
                  ? `Apply to ${selectedIds.size} selected unit(s)`
                  : `Apply to all ${children.length} units in this batch`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Action</Label>
                <Select value={lifecycleStatus} onValueChange={setLifecycleStatus}>
                  <SelectTrigger className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}>
                    <SelectItem value="decommissioned">Decommission</SelectItem>
                    <SelectItem value="blacklisted">Blacklist</SelectItem>
                    <SelectItem value="active">Restore (active)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reason (optional)</Label>
                <Input
                  value={lifecycleReason}
                  onChange={(e) => setLifecycleReason(e.target.value)}
                  placeholder="e.g. damaged label, recall batch"
                  className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLifecycleOpen(false)}>Cancel</Button>
              <Button onClick={handleLifecycleApply} disabled={submitting} className="bg-brand-600 hover:bg-brand-700 text-white">
                {submitting ? <CircleNotch className="w-4 h-4 animate-spin" /> : 'Apply'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Map batch dialog */}
        <Dialog open={mapOpen} onOpenChange={setMapOpen}>
          <DialogContent className={`${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-100' : 'bg-white'} max-w-md`}>
            <DialogHeader>
              <DialogTitle className="font-heading">{t('qr.mapToProductTitle')}</DialogTitle>
              <DialogDescription className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>
                {t('qr.mapToProductDesc')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('qr.mapProductSelect')} *</Label>
                <Select value={mapProductId} onValueChange={setMapProductId}>
                  <SelectTrigger className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="map-product-select">
                    <SelectValue placeholder={t('generateQr.chooseProd')} />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('qr.mapBatchNumber')}</Label>
                <Input value={mapBatchNumber} onChange={(e) => setMapBatchNumber(e.target.value)}
                  className={isDark ? 'bg-zinc-800 border-zinc-700' : ''} data-testid="map-batch-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t('qr.mapMfgDate')}</Label>
                  <Input type="date" value={mapMfgDate} onChange={(e) => setMapMfgDate(e.target.value)}
                    className={isDark ? 'bg-zinc-800 border-zinc-700' : ''} />
                </div>
                <div className="space-y-2">
                  <Label>{t('qr.mapExpiryDate')}</Label>
                  <Input type="date" value={mapExpiryDate} onChange={(e) => setMapExpiryDate(e.target.value)}
                    className={isDark ? 'bg-zinc-800 border-zinc-700' : ''} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMapOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleMapProduct} disabled={submitting || !mapProductId}
                className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="confirm-map-btn">
                {submitting ? <CircleNotch className="w-4 h-4 animate-spin" /> : t('qr.mapToProduct')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={subOpen} onOpenChange={setSubOpen}>
          <DialogContent className={`${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-100' : 'bg-white'} max-w-md`}>
            <DialogHeader>
              <DialogTitle className="font-heading">{t('qr.createSubMasterTitle')}</DialogTitle>
              <DialogDescription className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>
                {t('qr.createSubMasterDesc')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <p className={`text-sm ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                {selectedIds.size > 0
                  ? `${selectedIds.size} selected item QR(s) will be grouped.`
                  : `All ${children.length} item QR(s) in this batch will be grouped.`}
              </p>
              <div className="space-y-2">
                <Label>{t('qr.subMasterDestination')}</Label>
                <Input
                  value={subDestination}
                  onChange={(e) => setSubDestination(e.target.value)}
                  placeholder="e.g. Bangalore DC"
                  className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}
                  data-testid="submaster-destination-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('qr.notes')}</Label>
                <Input
                  value={subNotes}
                  onChange={(e) => setSubNotes(e.target.value)}
                  placeholder="Optional shipment notes"
                  className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSubOpen(false)}>{t('common.cancel')}</Button>
              <Button
                onClick={handleCreateSubMaster}
                disabled={submitting || children.length === 0}
                className="bg-brand-600 hover:bg-brand-700 text-white"
                data-testid="confirm-submaster-btn"
              >
                {submitting ? <CircleNotch className="w-4 h-4 animate-spin" /> : t('qr.createSubMaster')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <PrintPreviewDialog
          open={printPreviewOpen}
          onOpenChange={setPrintPreviewOpen}
          labels={printPreviewLabels}
          title={printPreviewTitle}
        />
      </div>
    </DashboardLayout>
  );
}
